import { useEffect, useState, useCallback, useRef } from "react";
import { Heart, Send, Loader2, Users, Mail, CalendarHeart, Palette, AlertTriangle, Wand2, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const emailValid = (e) => /.+@.+\..+/.test((e || "").trim());

/**
 * Dialogue « Remercier le magasin ».
 * Email de remerciement chaleureux, pré-rempli et brandé, déclenché À LA MAIN par
 * la CDP après un passage. Destinataire pré-rempli depuis la fiche du spot, mais
 * entièrement personnalisable (adresse, personnes en copie, objet, message).
 */
export function RemerciementDialog({ open, spot, associationId, onClose, onSent }) {
  const { user } = useAuth();
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [html, setHtml] = useState("");
  const [rendering, setRendering] = useState(false);
  const [sending, setSending] = useState(false);
  const [generating, setGenerating] = useState(false);       // génération du message par l'IA
  const [includeRenew, setIncludeRenew] = useState(true);   // bouton « choisir vos prochaines dates »
  const [renewUrl, setRenewUrl] = useState("");
  const [renewLoading, setRenewLoading] = useState(false);
  const [branding, setBranding] = useState(null);           // marque (logo/couleur) appliquée à l'email
  const [brandingLoading, setBrandingLoading] = useState(false);
  const seededFor = useRef(null);

  const spotName = spot?.normalized_name || spot?.enseigne || "votre magasin";
  const directeur = (() => {
    const c = spot?.contact_principal || {};
    const n = [c.civilite, c.nom].filter(Boolean).join(" ").trim();
    return n || "Madame, Monsieur";
  })();

  // Texte chaleureux par défaut (identique à l'esprit du modèle serveur).
  const defaultSubject = `Merci pour votre accueil chaleureux — ${spotName}`;
  const defaultBody = [
    `Bonjour ${directeur},`,
    `Je tenais à vous adresser un immense merci pour votre accueil lors de notre passage au ${spotName}. Vous nous avez reçus avec une gentillesse et une bienveillance qui nous ont profondément touchés.`,
    `Ces journées ont été un vrai bonheur : nous avons fait de très belles rencontres et eu de riches discussions avec vos clients, dans une ambiance chaleureuse et positive. Si vous saviez à quel point votre accueil a compté, pour nous comme pour la cause que nous portons !`,
    `Encore merci, du fond du cœur, pour votre confiance et votre hospitalité. Ce fut un réel plaisir, et nous espérons sincèrement avoir l'occasion de vous retrouver bientôt.`,
    `Chaleureusement,`,
  ].join("\n");

  const buildFacts = useCallback(() => ({
    spot: spotName,
    cdp: user?.name,
    nom_directeur: directeur,
    // Branding : l'email est aux couleurs de l'association concernée (logo + signature).
    // Si l'association n'est pas transmise (ex. fiche spot), le serveur la déduit du
    // dernier passage du magasin via `spot_id`.
    ...(associationId ? { association_id: associationId } : {}),
    ...(includeRenew && renewUrl ? { renew_url: renewUrl } : {}),
  }), [spotName, user, directeur, associationId, includeRenew, renewUrl]);

  const render = useCallback(async (o, c) => {
    setRendering(true);
    try {
      const { data } = await api.post("/emails/render", {
        type: "remerciement", objet: o, corps: c, facts: buildFacts(), spot_id: spot?.id,
      });
      setHtml(data.html);
    } catch (e) {
      /* aperçu non bloquant */
    } finally { setRendering(false); }
  }, [buildFacts, spot]);

  // Pré-remplissage à l'ouverture (une fois par spot).
  useEffect(() => {
    if (!open || !spot) return;
    if (seededFor.current === spot.id) return;
    seededFor.current = spot.id;
    const secondary = (spot.contacts_secondaires || []).map((x) => x.email).filter(Boolean);
    setTo(spot.contact_principal?.email || "");
    setCc(secondary.join(", "));
    setSubject(defaultSubject);
    setBody(defaultBody);
    render(defaultSubject, defaultBody);
  }, [open, spot, defaultSubject, defaultBody, render]);

  useEffect(() => { if (!open) { seededFor.current = null; setRenewUrl(""); setIncludeRenew(true); setBranding(null); } }, [open]);

  // Détecte, dès l'ouverture, la MARQUE qui sera appliquée à l'email (logo + couleur
  // de l'association concernée) pour l'afficher au CDP avant l'envoi — et signaler
  // clairement le cas où l'association n'a pas de logo (repli Shariti).
  useEffect(() => {
    if (!open || !spot) return;
    let cancel = false;
    (async () => {
      setBrandingLoading(true);
      try {
        const { data } = await api.get("/emails/thank-branding", {
          params: { spot_id: spot.id, association_id: associationId || undefined },
        });
        if (!cancel) setBranding(data);
      } catch {
        if (!cancel) setBranding(null);
      } finally {
        if (!cancel) setBrandingLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [open, spot, associationId]);

  // Crée (ou réutilise) l'accusé « renouvellement » -> lien public pour le bouton du mail.
  const ensureRenewUrl = useCallback(async () => {
    if (!includeRenew || !spot) return "";
    if (renewUrl) return renewUrl;
    try {
      const { data } = await api.post("/ack", { spot_id: spot.id, kind: "renouvellement" });
      if (data?.path) {
        const u = `${window.location.origin}${data.path}`;
        setRenewUrl(u);
        return u;
      }
    } catch { /* non bloquant : le mail part sans le bouton */ }
    return "";
  }, [includeRenew, renewUrl, spot]);

  useEffect(() => {
    if (!open || !spot || !includeRenew || renewUrl) return;
    let cancel = false;
    (async () => {
      setRenewLoading(true);
      await ensureRenewUrl();
      if (!cancel) setRenewLoading(false);
    })();
    return () => { cancel = true; };
  }, [open, spot, includeRenew, renewUrl, ensureRenewUrl]);

  // Re-render (aperçu) débattu quand l'objet/le corps/l'option dates change.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => { if (subject || body) render(subject, body); }, 450);
    return () => clearTimeout(t);
  }, [subject, body, open, includeRenew, renewUrl, render]);

  // Génère un message chaleureux et personnalisé PAR L'IA (même moteur que l'onglet
  // « Emails »). Le texte reste entièrement modifiable ensuite ; on peut revenir au
  // modèle par défaut à tout moment via « Texte par défaut ».
  const generateAI = async () => {
    if (!spot) return;
    setGenerating(true);
    try {
      const url = await ensureRenewUrl();
      const aid = associationId || branding?.association_id || undefined;
      const facts = {
        spot: spotName, cdp: user?.name, nom_directeur: directeur,
        ...(aid ? { association_id: aid } : {}),
        ...(includeRenew && url ? { renew_url: url } : {}),
      };
      const { data } = await api.post("/emails/generate", { type: "remerciement", facts });
      if (data?.objet) setSubject(data.objet);
      if (data?.corps) setBody(data.corps);
      if (data?.html) setHtml(data.html);
      toast.success("Message généré par l'IA");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la génération IA");
    } finally { setGenerating(false); }
  };

  // Revient au texte type par défaut (chaleureux, non-IA).
  const resetDefault = () => {
    setSubject(defaultSubject);
    setBody(defaultBody);
    render(defaultSubject, defaultBody);
    toast.success("Texte par défaut restauré");
  };

  const doSend = async () => {
    const dest = to.trim();
    if (!emailValid(dest)) { toast.error("Renseignez une adresse email de destinataire valide"); return; }
    const extra = cc.split(/[,;\s]+/).map((s) => s.trim()).filter((e) => emailValid(e));
    const ccList = Array.from(new Set(extra)).filter((e) => e && e !== dest);
    setSending(true);
    try {
      // IMPORTANT : on garantit le lien « prochaines dates » AVANT l'envoi (sinon
      // le HTML pré-affiché pourrait partir sans le bouton à cause d'une course de
      // rendu). On re-rend donc l'email côté serveur, avec le renew_url présent.
      const url = await ensureRenewUrl();
      const facts = {
        spot: spotName, cdp: user?.name, nom_directeur: directeur,
        ...(associationId ? { association_id: associationId } : {}),
        ...(includeRenew && url ? { renew_url: url } : {}),
      };
      let finalHtml = html;
      try {
        const { data } = await api.post("/emails/render", {
          type: "remerciement", objet: subject || defaultSubject, corps: body, facts, spot_id: spot?.id,
        });
        if (data?.html) finalHtml = data.html;
      } catch { /* on retombe sur l'aperçu déjà rendu */ }
      if (!finalHtml) { toast.error("Aperçu en cours de préparation…"); setSending(false); return; }
      await api.post("/emails/send", {
        spot_id: spot?.id, association_id: associationId || undefined,
        type: "remerciement", to: dest, cc: ccList,
        reply_to: user?.email, subject: subject || defaultSubject, body_html: finalHtml,
      });
      toast.success(`Message de remerciement envoyé à ${dest} 💛`);
      onSent && onSent();
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose && onClose(); }}>
      <DialogContent className="flex max-h-[92vh] w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-4xl [&>*]:min-w-0" data-testid="remerciement-dialog">
        <DialogHeader className="shrink-0 border-b border-[var(--border-soft)] bg-[#FFF7F1] px-5 py-4">
          <DialogTitle className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-full bg-[var(--shariti-orange)] text-white"><Heart className="h-4 w-4" /></span>
            Remercier le magasin
            <span className="ml-1 break-words text-sm font-semibold text-muted-ink">· {spotName}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="grid min-h-0 flex-1 gap-0 overflow-hidden md:grid-cols-2">
          {/* Colonne édition */}
          <div className="space-y-3 overflow-y-auto p-5">
            {/* Marque de l'email : logo + couleur de l'association (ou repli visible) */}
            <div className="rounded-xl border border-[var(--border-soft)] bg-white px-3 py-2.5" data-testid="remerciement-branding">
              <div className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-muted-ink">
                <Palette className="h-3 w-3" /> Marque de l'email
              </div>
              {brandingLoading ? (
                <div className="mt-2 flex items-center gap-2 text-xs text-muted-ink">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Détection de l'association…
                </div>
              ) : branding && branding.association_id ? (
                <div className="mt-2 flex items-center gap-3">
                  {branding.has_logo ? (
                    <img src={`${api.defaults.baseURL}/assets/association-logo/${branding.association_id}`}
                      alt={branding.name} data-testid="remerciement-branding-logo"
                      className="h-9 w-auto max-w-[120px] rounded object-contain" />
                  ) : (
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-[11px] font-black text-white"
                      style={{ backgroundColor: branding.color || "#EE722E" }} data-testid="remerciement-branding-initials">
                      {(branding.acronym || branding.name || "?").slice(0, 3).toUpperCase()}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 shrink-0 rounded-full ring-1 ring-black/10"
                        style={{ backgroundColor: branding.color || "#EE722E" }} title={branding.color} />
                      <span className="break-words text-sm font-bold text-ink" data-testid="remerciement-branding-name">{branding.name}</span>
                    </div>
                    {branding.will_fallback_to_shariti ? (
                      <span className="mt-0.5 flex items-center gap-1 text-[11px] font-semibold text-[#9B2226]" data-testid="remerciement-branding-warning">
                        <AlertTriangle className="h-3 w-3 shrink-0" /> Logo manquant : le logo Shariti sera utilisé par défaut.
                      </span>
                    ) : (
                      <span className="mt-0.5 block text-[11px] text-muted-ink">Bande, boutons et signature aux couleurs de cette association.</span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="mt-2 flex items-center gap-1.5 text-[11px] font-semibold text-[#9B2226]" data-testid="remerciement-branding-warning">
                  <AlertTriangle className="h-3 w-3 shrink-0" /> Aucune association détectée : l'email utilisera la marque Shariti.
                </div>
              )}
            </div>
            <div>
              <Label className="flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-muted-ink"><Mail className="h-3 w-3" /> Destinataire</Label>
              <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="responsable@magasin.fr"
                className="mt-1" data-testid="remerciement-to" />
              {!emailValid(to) && (
                <p className="mt-1 text-[11px] font-medium text-[#9B2226]">Aucune adresse valide — renseignez le responsable du magasin.</p>
              )}
            </div>
            <div>
              <Label className="flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-muted-ink"><Users className="h-3 w-3" /> En copie (facultatif)</Label>
              <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="collègue@shariti.com, adjoint@magasin.fr"
                className="mt-1" data-testid="remerciement-cc" />
              <p className="mt-1 text-[11px] text-muted-ink">Séparez les adresses par une virgule.</p>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Objet</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1" data-testid="remerciement-subject" />
            </div>
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-[var(--border-soft)] bg-white px-3 py-2.5" data-testid="remerciement-ai-toolbar">
              <span className="text-[11px] font-bold uppercase tracking-wide text-muted-ink">Rédaction du message</span>
              <div className="ml-auto flex items-center gap-2">
                <Button type="button" size="sm" onClick={generateAI} disabled={generating || rendering}
                  className="h-8 gap-1.5 bg-[var(--shariti-orange)] font-semibold text-white hover:bg-[#E56622]" data-testid="remerciement-generate-ai">
                  {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
                  Générer avec l'IA
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={resetDefault} disabled={generating}
                  className="h-8 gap-1.5" data-testid="remerciement-reset-default">
                  <RotateCcw className="h-3.5 w-3.5" /> Texte par défaut
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Message</Label>
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={12}
                className="mt-1 min-h-[220px] leading-relaxed" data-testid="remerciement-body" />
              <p className="mt-1 text-[11px] text-muted-ink">La signature (vous + l'association concernée) et le bandeau « Merci ! » sont ajoutés automatiquement.</p>
            </div>

            <label className="flex items-start gap-3 rounded-xl border border-[var(--border-soft)] bg-[#FFF7F1] px-3 py-3" data-testid="remerciement-renew-row">
              <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-white text-[var(--shariti-orange)]"><CalendarHeart className="h-4 w-4" /></span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center justify-between gap-2">
                  <span className="text-sm font-bold text-[#8A3A10]">Proposer de choisir ses prochaines dates</span>
                  <Switch checked={includeRenew} onCheckedChange={setIncludeRenew} data-testid="remerciement-renew-switch" />
                </span>
                <span className="mt-0.5 block text-[11px] leading-relaxed text-muted-ink">
                  Ajoute un bouton bienveillant : le magasin choisit lui-même, sans engagement, les dates d'une prochaine venue.
                  {renewLoading && <span className="ml-1 text-[var(--shariti-orange)]">préparation du lien…</span>}
                </span>
              </span>
            </label>
          </div>

          {/* Colonne aperçu */}
          <div className="flex flex-col overflow-hidden border-t border-[var(--border-soft)] bg-[var(--surface-2)] md:border-l md:border-t-0">
            <div className="flex items-center justify-between px-4 pt-3 text-xs font-bold uppercase tracking-wide text-muted-ink">
              <span className="flex items-center gap-1"><Heart className="h-3 w-3 text-[var(--shariti-orange)]" /> Aperçu</span>
              {rendering && <Loader2 className="h-3.5 w-3.5 animate-spin text-[var(--shariti-orange)]" />}
            </div>
            <div className="flex-1 overflow-hidden p-4">
              <iframe title="apercu-remerciement" srcDoc={html}
                className="h-full min-h-[360px] w-full rounded-xl border border-[var(--border-soft)] bg-white" data-testid="remerciement-preview" />
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-[var(--border-soft)] bg-white px-5 py-3">
          <Button variant="outline" onClick={onClose} data-testid="remerciement-cancel">Annuler</Button>
          <Button onClick={doSend} disabled={sending || !emailValid(to)}
            className="gap-2 bg-[var(--shariti-orange)] font-semibold text-white hover:bg-[#E56622]" data-testid="remerciement-send">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Envoyer le remerciement
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
